import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable"; // ✅ Importación explícita
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import SectionHeader from "../components/ui/SectionHeader";
import { useAuth } from "../context/AuthContext";

export default function ModuloReportes({ equipos, tickets, presupuestos, auditorias, onNotify }) {
  const { usuario } = useAuth();
  const [generando, setGenerando] = useState(false);
  const [ setReportes] = useState([
    { id: 1, tipo: "Reporte ANMAT mensual", fecha: "2026-05-01", estado: "Enviado", periodo: "Abril 2026" },
    { id: 2, tipo: "Dashboard KPIs", fecha: "2026-05-01", estado: "Enviado", periodo: "Abril 2026" },
    { id: 3, tipo: "Auditorías exportadas", fecha: "2026-04-15", estado: "Enviado", periodo: "Q1 2026" },
  ]);

  const generar = (tipo) => {
    setGenerando(true);
    setTimeout(() => {
      try {
        descargarPDF(tipo);
        setReportes(prev => [{ 
          id: Date.now(), 
          tipo: tipo === 'anmat' ? "Reporte ANMAT mensual" : tipo === 'kpis' ? "Dashboard KPIs" : "Auditorías exportadas", 
          fecha: new Date().toISOString().slice(0, 10), 
          estado: "Descargado",
          periodo: "Actual" 
        }, ...prev]);
        onNotify?.("PDF generado y descargado", "success");
      } catch (error) {
        console.error("Error generando PDF:", error);
        onNotify?.("Error al generar el PDF. Revisá consola.", "error");
      }
      setGenerando(false);
    }, 800);
  };

  const descargarPDF = (tipo) => {
    const doc = new jsPDF();
    const fechaHoy = new Date().toLocaleDateString('es-AR');
    const horaHoy = new Date().toLocaleTimeString('es-AR');

    // ── HEADER COMÚN ──
    doc.setFillColor(15, 76, 129);
    doc.rect(0, 0, 210, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.text("CARDIOHARD ANMAT", 14, 16);
    doc.setFontSize(10);
    doc.text(`Generado: ${fechaHoy} ${horaHoy} | Usuario: ${usuario?.nombre || 'Sistema'}`, 14, 22);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);

    // ── LÓGICA POR TIPO ──
    if (tipo === 'anmat') {
      doc.text("Resumen Ejecutivo de Equipos y Tickets", 14, 40);

      const equiposData = equipos.map(e => [e.serie, e.modelo, e.fabricante, e.estado]);
      // ✅ Usamos autoTable(doc, {...}) en lugar de doc.autoTable({...})
      autoTable(doc, {
        startY: 45,
        head: [['Serie', 'Modelo', 'Fabricante', 'Estado']],
        body: equiposData,
        theme: 'grid',
        headStyles: { fillColor: [15, 76, 129] },
        title: "Inventario de Equipos",
        margin: { left: 14 }
      });

      const ticketsData = tickets.map(t => [`#${t.id}`, t.tecnico, t.estado, t.fecha || '—']);
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [['ID', 'Técnico', 'Estado', 'Fecha']],
        body: ticketsData,
        theme: 'grid',
        headStyles: { fillColor: [15, 76, 129] },
        title: "Estado de Tickets",
        margin: { left: 14 }
      });

      doc.save(`Reporte_ANMAT_${fechaHoy.replace(/\//g, '-')}.pdf`);

    } else if (tipo === 'auditorias') {
      doc.text("Registro de Trazabilidad y Auditorías", 14, 40);

      const auditData = auditorias.map(a => [a.fecha, a.usuario, a.accion, a.modulo, a.resultado]);
      autoTable(doc, {
        startY: 45,
        head: [['Fecha', 'Usuario', 'Acción', 'Módulo', 'Resultado']],
        body: auditData,
        theme: 'grid',
        headStyles: { fillColor: [15, 76, 129] },
        margin: { left: 14 }
      });

      doc.save(`Auditorias_${fechaHoy.replace(/\//g, '-')}.pdf`);

    } else if (tipo === 'kpis') {
      doc.text("Indicadores Clave de Desempeño (KPIs)", 14, 40);

      const abiertos = tickets.filter(t => !["Cerrado", "Entregado"].includes(t.estado)).length;
      const enReparacion = equipos.filter(e => e.estado === "En reparación").length;
      
      doc.setFontSize(11);
      doc.text(`• Tickets Abiertos actualmente: ${abiertos}`, 14, 55);
      doc.text(`• Equipos en Reparación: ${enReparacion}`, 14, 62);
      doc.text(`• Total Presupuestos: ${presupuestos.length}`, 14, 69);
      doc.text(`• Tickets en Validación: ${tickets.filter(t => t.estado === "Validación").length}`, 14, 76);

      doc.text("Nota: Para gráficos visuales, consultar el Dashboard en pantalla.", 14, 90);
      
      doc.save(`KPIs_Resumen_${fechaHoy.replace(/\//g, '-')}.pdf`);
    }
  };

  return (
    <div>
      <SectionHeader title="Centro de Reportes" />
      <p className="text-muted mb-5">Genera documentación oficial para ANMAT y control interno en formato PDF.</p>

      <div className="grid grid--3 gap-3 mb-5">
        <Card className="text-center p-4">
          <div className="text-4xl mb-2">📊</div>
          <p className="font-semibold mb-1">Reporte General ANMAT</p>
          <p className="text-xs text-muted mb-4">Equipos + Tickets + Estado actual</p>
          <Btn variant="primary" className="w-full" onClick={() => generar('anmat')} disabled={generando}>
            {generando ? "⏳ Generando..." : "📄 Descargar PDF"}
          </Btn>
        </Card>

        <Card className="text-center p-4">
          <div className="text-4xl mb-2">📈</div>
          <p className="font-semibold mb-1">Resumen KPIs</p>
          <p className="text-xs text-muted mb-4">Métricas numéricas del sistema</p>
          <Btn variant="primary" className="w-full" onClick={() => generar('kpis')} disabled={generando}>
            {generando ? "⏳ Generando..." : "📄 Descargar PDF"}
          </Btn>
        </Card>

        <Card className="text-center p-4">
          <div className="text-4xl mb-2">📋</div>
          <p className="font-semibold mb-1">Log de Auditorías</p>
          <p className="text-xs text-muted mb-4">Historial completo de acciones</p>
          <Btn variant="primary" className="w-full" onClick={() => generar('auditorias')} disabled={generando}>
            {generando ? "⏳ Generando..." : "📄 Descargar PDF"}
          </Btn>
        </Card>
      </div>

      <Card>
        <SectionHeader title="Historial de descargas recientes" />
        <div className="text-sm text-muted text-center py-2">
           Los archivos se descargan directamente a tu carpeta de "Descargas".
        </div>
      </Card>
    </div>
  );
}